/*
 * Cold Black Coffee theme script. Accordions, quantity steppers, the cart drawer, motion
 * (scroll reveals, the proof count-up, the header colour), the buy box and the sticky bar.
 * Loaded with defer at the end of body. No imports, no dependencies.
 */
(() => {
  const root = document.documentElement;
  root.classList.add('js');

  const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const SECTION = 'cart-drawer';

  /* Helpers ---------------------------------------------------------- */

  const decode = (html) => {
    const el = document.createElement('textarea');
    el.innerHTML = html;
    return el.value;
  };

  function formatMoney(cents, format) {
    const value = Number(cents) || 0;
    const fmt = (decimals, thousands = ',', decimal = '.') => {
      const [whole, fraction] = (value / 100).toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    };
    return decode(format || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return fmt(0);
        case 'amount_with_comma_separator':
          return fmt(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return fmt(0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return fmt(2, "'", '.');
        case 'amount_with_space_separator':
          return fmt(2, ' ', ',');
        case 'amount_no_decimals_with_space_separator':
          return fmt(0, ' ', ',');
        default:
          return fmt(2);
      }
    });
  }

  const toast = {
    timer: 0,
    show(message) {
      const el = document.getElementById('SiteToast');
      if (!el || !message) return;
      el.querySelector('[data-toast-message]').textContent = message;
      el.hidden = false;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.hide(), 6000);
    },
    hide() {
      const el = document.getElementById('SiteToast');
      if (el) el.hidden = true;
    },
  };

  async function postJSON(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) throw new Error(data.description || data.message || '');
    return data;
  }

  /* Accordions ------------------------------------------------------- */

  document.addEventListener('click', (event) => {
    const button = event.target.closest('.accordion__button');
    if (!button) return;
    const panel = document.getElementById(button.getAttribute('aria-controls'));
    const open = button.getAttribute('aria-expanded') !== 'true';
    button.setAttribute('aria-expanded', String(open));
    if (panel) panel.classList.toggle('is-open', open);
  });

  /* Quantity steppers ------------------------------------------------ */

  function syncStepper(wrapper) {
    if (!wrapper) return;
    const input = wrapper.querySelector('.quantity__input');
    const value = Number(input.value);
    const [minus, plus] = wrapper.querySelectorAll('.quantity__button');
    if (minus) minus.disabled = value <= Number(input.min || 0);
    if (plus) plus.disabled = input.max !== '' && value >= Number(input.max);
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-quantity-step]');
    if (!button) return;
    const input = button.closest('[data-quantity]').querySelector('.quantity__input');
    const min = Number(input.min || 0);
    const max = input.max === '' ? Infinity : Number(input.max);
    const next = Math.min(max, Math.max(min, (Number(input.value) || 0) + Number(button.dataset.quantityStep)));
    if (next === Number(input.value)) return;
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  document.addEventListener('change', (event) => {
    const wrapper = event.target.closest('[data-quantity]');
    if (wrapper) syncStepper(wrapper);
  });

  document.querySelectorAll('[data-quantity]').forEach(syncStepper);

  /* Cart drawer ------------------------------------------------------ */

  const drawer = {
    returnFocus: null,

    get el() {
      return document.getElementById('CartDrawer');
    },

    get body() {
      return this.el && this.el.querySelector('[data-cart-body]');
    },

    isOpen() {
      return Boolean(this.el) && !this.el.hidden;
    },

    open(trigger) {
      const el = this.el;
      if (!el || this.isOpen()) return;
      this.returnFocus = trigger || document.activeElement;
      root.style.setProperty('--scrollbar-width', `${window.innerWidth - root.clientWidth}px`);
      root.classList.add('is-locked');
      el.hidden = false;
      this.setInert(true);
      el.querySelector('.drawer__close').focus();
      document.dispatchEvent(new CustomEvent('cart:drawer', { detail: { open: true } }));
    },

    close() {
      if (!this.isOpen()) return;
      this.el.hidden = true;
      root.classList.remove('is-locked');
      root.style.removeProperty('--scrollbar-width');
      this.setInert(false);
      const target =
        this.returnFocus && document.contains(this.returnFocus) && this.returnFocus.offsetParent !== null
          ? this.returnFocus
          : document.querySelector('[data-cart-link]');
      if (target) target.focus();
      document.dispatchEvent(new CustomEvent('cart:drawer', { detail: { open: false } }));
    },

    setInert(on) {
      [
        document.getElementById('MainContent'),
        document.querySelector('.skip-link'),
        ...document.querySelectorAll('.shopify-section-group-header-group, .shopify-section-group-footer-group'),
      ].forEach((node) => {
        if (node) node.inert = on;
      });
    },

    trap(event) {
      const focusable = [
        ...this.el.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'),
      ].filter((node) => node.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || !this.el.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },

    /* Swap the body with Section Rendering API HTML, then update the header count. */
    render(html) {
      const current = this.body;
      if (!current || !html) return null;
      const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-body]');
      if (!next) return null;
      const hadFocus = current.contains(document.activeElement);
      current.replaceWith(next);
      next.querySelectorAll('[data-quantity]').forEach(syncStepper);
      document.querySelectorAll('[data-cart-count]').forEach((node) => {
        node.textContent = next.dataset.merchCount;
      });
      if (hadFocus && this.isOpen()) this.el.querySelector('.drawer__panel').focus();
      return next;
    },

    async refresh() {
      const html = await fetch(`${window.location.pathname}?section_id=${SECTION}`).then((r) => r.text());
      return this.render(html);
    },

    /* The fees app can add its line a moment after the add. One retry, 500 ms later. */
    async checkFee(body) {
      if (!body || body.dataset.hasFee === 'true' || body.dataset.merchCount === '0') return;
      await new Promise((resolve) => setTimeout(resolve, 500));
      const feeProduct = body.dataset.feeProduct;
      const cart = await fetch(`${rootUrl}cart.js`).then((r) => r.json());
      const hasFee = cart.items.some(
        (item) =>
          item.product_type === 'Fee Placeholder' ||
          (feeProduct && String(item.product_id) === feeProduct) ||
          (item.properties && item.properties._mf_fee_id),
      );
      if (hasFee) await this.refresh();
    },

    async change(body) {
      const panel = this.el.querySelector('.drawer__panel');
      const errorMessage = this.body && this.body.dataset.error;
      panel.setAttribute('aria-busy', 'true');
      try {
        const data = await postJSON(`${rootUrl}cart/change.js`, { ...body, sections: SECTION });
        await this.checkFee(this.render(data.sections && data.sections[SECTION]));
      } catch (error) {
        toast.show(error.message || errorMessage);
        await this.refresh();
      } finally {
        panel.removeAttribute('aria-busy');
      }
    },
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-cart-link]');
    if (link && drawer.el) {
      event.preventDefault();
      drawer.open(link);
      return;
    }

    if (event.target.closest('[data-drawer-close]')) {
      const anchor = event.target.closest('a[href]');
      drawer.close();
      // "Order now" in the empty drawer: stay on the page when the buy box is here.
      const buyBox = anchor && anchor.hash === '#buy-box' && document.getElementById('buy-box');
      if (buyBox) {
        event.preventDefault();
        buyBox.scrollIntoView();
        buyBox.focus({ preventScroll: true });
      }
      return;
    }

    const remove = event.target.closest('[data-line-remove]');
    if (remove) {
      drawer.change({ id: remove.dataset.lineRemove, quantity: 0 });
      return;
    }

    const upsell = event.target.closest('[data-upsell]');
    if (upsell) {
      upsell.disabled = true;
      const { upsell: kind, lineKey, variant, upsellQuantity: quantity, sellingPlan } = upsell.dataset;
      if (kind === 'swap') {
        const item = { id: Number(variant), quantity: Number(quantity) };
        if (sellingPlan) item.selling_plan = Number(sellingPlan);
        postJSON(`${rootUrl}cart/add.js`, { items: [item] })
          .then(() => drawer.change({ id: lineKey, quantity: 0 }))
          .catch((error) => {
            toast.show(error.message || (drawer.body && drawer.body.dataset.error));
            drawer.refresh();
          });
      } else if (kind === 'subscribe') {
        drawer.change({ id: lineKey, quantity: Number(quantity), selling_plan: Number(sellingPlan) });
      }
    }
  });

  let lineTimer = 0;
  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-line-quantity]');
    if (!input) return;
    clearTimeout(lineTimer);
    lineTimer = setTimeout(() => {
      drawer.change({ id: input.dataset.lineKey, quantity: Math.max(0, Number(input.value) || 0) });
    }, 300);
  });

  document.addEventListener('keydown', (event) => {
    if (!drawer.isOpen()) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      drawer.close();
    } else if (event.key === 'Tab') {
      drawer.trap(event);
    }
  });

  /* Motion ----------------------------------------------------------- */

  // Keyframes, durations and delays follow the homepage design. Reduced motion keeps the
  // page still, except the header colour, which is state rather than motion.
  const EASE = 'cubic-bezier(0.2, 0, 0, 1)';
  const motion =
    typeof Element.prototype.animate === 'function' &&
    'IntersectionObserver' in window &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // cubic-bezier(0.2, 0, 0, 1), the --ease-out token, as in src/can-hero.js. Newton on x.
  function easeOut(x) {
    const x1 = 0.2;
    const x2 = 0;
    const y1 = 0;
    const y2 = 1;
    const bx = (t) => 3 * (1 - t) * (1 - t) * t * x1 + 3 * (1 - t) * t * t * x2 + t * t * t;
    const dx = (t) => 3 * (1 - t) * (1 - t) * x1 + 6 * (1 - t) * t * (x2 - x1) + 3 * t * t * (1 - x2);
    let t = x;
    for (let i = 0; i < 6; i++) {
      const d = dx(t);
      if (Math.abs(d) < 1e-6) break;
      t = Math.min(1, Math.max(0, t - (bx(t) - x) / d));
    }
    return 3 * (1 - t) * (1 - t) * t * y1 + 3 * (1 - t) * t * t * y2 + t * t * t;
  }

  const REVEALS = {
    up: { keyframes: [{ opacity: 0, transform: 'translateY(32px)' }, { opacity: 1, transform: 'none' }], duration: 700 },
    rise: { keyframes: [{ opacity: 0, transform: 'translateY(64px)' }, { opacity: 1, transform: 'none' }], duration: 1000 },
    mask: { keyframes: [{ transform: 'translateY(105%)' }, { transform: 'none' }], duration: 820 },
    line: { keyframes: [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], duration: 1100 },
    clip: { keyframes: [{ clipPath: 'inset(100% 0 0 0)' }, { clipPath: 'inset(0 0 0 0)' }], duration: 1200 },
  };

  /*
   * data-rv="up|rise|mask|line|clip" holds an element on its first frame until it scrolls
   * into view; data-d delays it (ms). A masked, clipped or rising element has no box of its
   * own to see yet, so its parent is watched instead. On a container, data-rv-stagger="ms"
   * adds ms x the index of the child an element sits in (capped at data-rv-stagger-max),
   * and data-rv-each="kind" reveals each child, for rich text.
   */
  function setUpReveals() {
    document.querySelectorAll('[data-rv-each]').forEach((container) => {
      [...container.children].forEach((child) => {
        if (!child.dataset.rv) child.dataset.rv = container.dataset.rvEach;
      });
    });

    // Measure everything before the first animation applies, so layout is read once.
    // Whatever is already on screen or above it (a restored scroll position) stays put.
    const items = [...document.querySelectorAll('[data-rv]')]
      .filter((el) => REVEALS[el.dataset.rv])
      .map((el) => {
        const kind = el.dataset.rv;
        const target = kind === 'up' || kind === 'line' ? el : el.parentElement;
        return { el, kind, target, below: target.getBoundingClientRect().top >= window.innerHeight };
      })
      .filter((item) => item.below);
    if (!items.length) return;

    const pending = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          pending.get(entry.target).forEach((animation) => animation.play());
          pending.delete(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px' },
    );

    items.forEach(({ el, kind, target }) => {
      let delay = Number(el.dataset.d) || 0;
      const group = el.parentElement.closest('[data-rv-stagger]');
      if (group) {
        const index = [...group.children].findIndex((child) => child.contains(el));
        const max = Number(group.dataset.rvStaggerMax) || Infinity;
        delay += Math.min(index, max) * Number(group.dataset.rvStagger);
      }
      const { keyframes, duration } = REVEALS[kind];
      const animation = el.animate(keyframes, { duration, delay, easing: EASE, fill: 'both' });
      animation.pause();
      // Played out, the element gets its own styles back: no fill left over it.
      animation.addEventListener('finish', () => animation.cancel());
      if (!pending.has(target)) {
        pending.set(target, []);
        observer.observe(target);
      }
      pending.get(target).push(animation);
    });
  }

  // Proof strip: the [data-count] numbers count up from 0 once the strip is 40% in view,
  // with their own decimals and separator. Screen readers read the full text instead.
  function setUpCountUp(strip) {
    if (strip.getBoundingClientRect().top < window.innerHeight) return;
    const numbers = [...strip.querySelectorAll('[data-count]')]
      .map((node) => {
        const text = node.textContent.trim();
        const separator = text.includes(',') ? ',' : '.';
        const decimals = (text.split(separator)[1] || '').length;
        return { node, text, separator, decimals, target: parseFloat(text.replace(',', '.')) };
      })
      .filter(({ target }) => Number.isFinite(target));
    if (!numbers.length) return;

    const show = (k) => {
      numbers.forEach(({ node, text, separator, decimals, target }) => {
        node.textContent = k >= 1 ? text : (target * k).toFixed(decimals).replace('.', separator);
      });
    };
    show(0);

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        const start = performance.now() + 150;
        const step = (now) => {
          const k = Math.min(1, Math.max(0, (now - start) / 1600));
          show(k < 1 ? easeOut(k) : 1);
          if (k < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      },
      { threshold: 0.4 },
    );
    observer.observe(strip);
  }

  // The announcement bar and the header take the colours of the section under them:
  // theme-inverse while a white section crosses the header's bottom edge.
  function setUpHeaderTheme() {
    const header = document.querySelector('.header');
    if (!header) return;
    const bars = [document.querySelector('.announcement'), header].filter(Boolean);
    let light = [];
    let frame = 0;

    const paint = () => {
      frame = 0;
      const edge = header.getBoundingClientRect().bottom;
      const on = light.some((section) => {
        const box = section.getBoundingClientRect();
        return box.top <= edge && box.bottom > edge;
      });
      bars.forEach((bar) => bar.classList.toggle('theme-inverse', on));
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(paint);
    };
    const collect = () => {
      light = [...document.querySelectorAll('#MainContent > .shopify-section.theme-inverse')];
      schedule();
    };

    collect();
    window.addEventListener(
      'scroll',
      () => {
        if (light.length) schedule();
      },
      { passive: true },
    );
    window.addEventListener('resize', schedule);
    ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((type) => {
      document.addEventListener(type, collect);
    });
  }

  setUpHeaderTheme();

  if (motion) {
    setUpReveals();
    document.querySelectorAll('[data-count-up]').forEach(setUpCountUp);

    // A new Pack or plan: the price fades up.
    document.addEventListener('change', (event) => {
      if (!event.target.matches('[data-pack-input], [data-plan-input]')) return;
      const box = event.target.closest('[data-buy-box]');
      const price = box && box.querySelector('.buy-box__price .price');
      if (!price) return;
      price.animate([{ opacity: 0, transform: 'translateY(10px)' }, { opacity: 1, transform: 'none' }], {
        duration: 320,
        easing: EASE,
      });
    });

    // The cart count bumps when it changes.
    const cartLink = document.querySelector('.header__cart');
    const cartCount = cartLink && cartLink.querySelector('[data-cart-count]');
    if (cartCount) {
      let count = cartCount.textContent;
      new MutationObserver(() => {
        if (cartCount.textContent === count) return;
        count = cartCount.textContent;
        cartLink.animate([{ transform: 'translateY(-6px)', opacity: 0.3 }, { transform: 'none', opacity: 1 }], {
          duration: 420,
          easing: EASE,
        });
      }).observe(cartCount, { childList: true, characterData: true, subtree: true });
    }
  }

  /* Buy box ---------------------------------------------------------- */

  const buyBox = document.querySelector('[data-buy-box]');
  const form = document.getElementById('BuyBoxForm');
  const dataNode = buyBox && buyBox.querySelector('[data-buy-box-data]');
  if (!buyBox || !form || !dataNode) return;

  const data = JSON.parse(dataNode.textContent);
  const money = (cents) => formatMoney(cents, data.moneyFormat);
  const fill = (template, amount) => template.replace('__AMOUNT__', amount);
  const sticky = buyBox.querySelector('[data-sticky-buy]');
  const quantityInput = form.querySelector('[data-buy-quantity]');
  const submits = [
    { button: form.querySelector('[data-buy-submit]'), label: form.querySelector('[data-buy-label]') },
  ];
  if (sticky) {
    submits.push({ button: sticky.querySelector('[data-sticky-submit]'), label: sticky.querySelector('[data-sticky-label]') });
  }
  let busy = false;

  const state = () => {
    const pack = form.querySelector('[data-pack-input]:checked');
    const plan = form.querySelector('[data-plan-input]:checked');
    const variant = pack ? data.variants[pack.value] : null;
    const planId = plan ? plan.value : '';
    const allocation = variant && planId ? variant.plans[planId] : null;
    return { pack, variant, planId, allocation };
  };

  function update() {
    const { pack, variant, planId, allocation } = state();
    if (!variant) return;

    // Quantity limit follows the selected variant.
    quantityInput.max = variant.max;
    if (Number(quantityInput.value) > variant.max) quantityInput.value = variant.max;
    syncStepper(quantityInput.closest('[data-quantity]'));
    const quantity = Math.max(1, Number(quantityInput.value) || 1);

    // Price, compare-at (subscription only), per-Can.
    const unitPrice = allocation ? allocation.price : variant.price;
    const showCompare = Boolean(allocation) && allocation.compare > allocation.price;
    buyBox.querySelector('[data-price-amount]').textContent = money(unitPrice);
    buyBox.querySelector('[data-price-compare-wrap]').hidden = !showCompare;
    buyBox.querySelector('[data-price-compare]').textContent = showCompare ? money(allocation.compare) : '';
    const saleLabel = buyBox.querySelector('[data-price-sale-label]');
    if (saleLabel) saleLabel.hidden = !showCompare;
    const unit = buyBox.querySelector('[data-price-unit]');
    unit.hidden = !(variant.cans > 0);
    unit.textContent = variant.cans > 0 ? fill(data.strings.perCan, money(Math.round(unitPrice / variant.cans))) : '';

    // Statiegeld: cans x quantity x deposit, in the buy box and the sticky bar.
    const deposit = fill(data.strings.statiegeld, money(variant.cans * quantity * data.deposit));
    buyBox.querySelectorAll('[data-statiegeld-line], .sticky-buy__statiegeld').forEach((node) => {
      node.textContent = deposit;
      node.hidden = !(variant.cans > 0 && data.deposit > 0);
    });

    // Purchase tile prices follow the Pack.
    form.querySelectorAll('[data-plan-input]').forEach((input) => {
      const aside = input.closest('.radio-tile').querySelector('[data-tile-aside]');
      const id = input.dataset.planId;
      if (!id) {
        if (aside) aside.textContent = money(variant.price);
        return;
      }
      const planAllocation = variant.plans[id];
      input.disabled = !planAllocation;
      if (aside) aside.textContent = planAllocation ? money(planAllocation.price) : '';
    });

    if (!busy) {
      submits.forEach(({ button, label }) => {
        button.disabled = !variant.available;
        label.textContent = variant.available ? fill(data.strings.add, money(unitPrice * quantity)) : data.strings.soldOut;
      });
    }

    buyBox.classList.toggle('is-subscription', Boolean(planId));

    if (sticky) {
      sticky.querySelector('[data-sticky-amount]').textContent = money(unitPrice);
      sticky.querySelectorAll('[data-sticky-pack]').forEach((label) => {
        label.classList.toggle('is-selected', label.dataset.stickyPack === pack.value);
      });
    }

    // Product page: keep ?variant= in step with the Pack.
    if (buyBox.dataset.productUrl) {
      const url = new URL(window.location.href);
      if (url.searchParams.get('variant') !== pack.value) {
        url.searchParams.set('variant', pack.value);
        window.history.replaceState(window.history.state, '', url);
      }
    }
  }

  form.addEventListener('change', update);
  quantityInput.addEventListener('input', update);
  update();

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const { variant } = state();
    if (busy || !variant || !variant.available) return;
    busy = true;
    toast.hide();
    submits.forEach(({ button, label }) => {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      label.textContent = data.strings.adding;
    });

    const body = new FormData(form);
    if (!body.get('selling_plan')) body.delete('selling_plan');
    body.append('sections', SECTION);
    body.append('sections_url', window.location.pathname);

    try {
      const response = await fetch(`${rootUrl}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.status) throw new Error(result.description || result.message || data.strings.error);
      if (drawer.el) {
        const next = drawer.render(result.sections && result.sections[SECTION]);
        drawer.open(event.submitter || submits[0].button);
        drawer.checkFee(next);
      } else {
        window.location.href = `${rootUrl}cart`;
      }
    } catch (error) {
      toast.show(error.message || data.strings.error);
    } finally {
      busy = false;
      submits.forEach(({ button }) => button.removeAttribute('aria-busy'));
      update();
    }
  });

  /* Sticky bar: mobile only (CSS). Shows once the buy box submit has scrolled past. */
  if (sticky) {
    const mainSubmit = submits[0].button;
    let buyBoxVisible = true;
    let drawerOpen = false;
    let frame = 0;

    const place = () => {
      frame = 0;
      const past = mainSubmit.getBoundingClientRect().bottom < 0;
      const visible = past && !buyBoxVisible && !drawerOpen;
      if (visible === sticky.classList.contains('is-visible')) return;
      sticky.classList.toggle('is-visible', visible);
      document.body.style.paddingBottom = visible && sticky.offsetHeight ? `${sticky.offsetHeight}px` : '';
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(place);
    };

    // A label click would focus the buy box radio and scroll the page back to it.
    sticky.addEventListener('click', (event) => {
      const label = event.target.closest('[data-sticky-pack]');
      if (!label) return;
      event.preventDefault();
      const input = document.getElementById(label.htmlFor);
      if (!input || input.disabled || input.checked) return;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    new IntersectionObserver((entries) => {
      buyBoxVisible = entries[0].isIntersecting;
      schedule();
    }).observe(buyBox);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    document.addEventListener('cart:drawer', (event) => {
      drawerOpen = event.detail.open;
      schedule();
    });
  }
})();
